// Persisted org-context reader/writer (spec 022 FR-011, SC-006; 027 Slice B
// reader migration FR-009/FR-011, SC-004/SC-005).
//
// SOURCE OF TRUTH (027 Slice B): when the EngagementProvider has resolved a
// current engagement, `readOrgContext()` reads over the provider's module-level
// SNAPSHOT (getEngagementSnapshot().organisation) — the engagement is the
// aggregate root. When there is NO snapshot (null — first-run / safe state / no
// provider mounted yet) it falls back to the legacy
// localStorage["arcpilot.orgContext"] home so the read shape is preserved
// exactly. This is a `readStored`-style util (mirrors VisionBoard's defensive
// reader): jsdom/SSR-safe and migration-tolerant — a missing / partial / garbage
// source NEVER throws and yields a safe empty shape.

import { getEngagementSnapshot } from '../hooks/useEngagement';

export const ORG_CONTEXT_KEY = 'arcpilot.orgContext';

export interface Organisation {
  name: string;
  descriptor?: string;
  website?: string;
}

export interface Background {
  text: string;
  provenance: 'stated' | 'inferred';
  confirmed: boolean;
  flaggedClaims?: string[];
  sources?: { title: string; url: string }[];
}

export interface PainPoint {
  id: string;
  title: string;
  detail?: string;
  whoFeelsIt?: string;
}

export interface OrgContext {
  organisation: Organisation | null;
  background: Background | null;
  painPoints: PainPoint[];
}

function emptyContext(): OrgContext {
  return { organisation: null, background: null, painPoints: [] };
}

// Apply the (shared) defensive parse to an already-deserialised org-context
// record, whether it came from the engagement snapshot or from localStorage JSON.
function parseOrgRecord(parsed: unknown): OrgContext {
  if (!parsed || typeof parsed !== 'object') return emptyContext();
  const record = parsed as Partial<OrgContext>;
  return {
    organisation:
      record.organisation && typeof record.organisation === 'object'
        ? (record.organisation as Organisation)
        : null,
    background:
      record.background && typeof record.background === 'object'
        ? (record.background as Background)
        : null,
    painPoints: Array.isArray(record.painPoints) ? (record.painPoints as PainPoint[]) : [],
  };
}

export function readOrgContext(): OrgContext {
  // 027 Slice B: prefer the engagement snapshot when present; else fall back to
  // the legacy localStorage home (preserving the pre-migration read shape).
  const snapshot = getEngagementSnapshot();
  if (snapshot) {
    try {
      return parseOrgRecord(snapshot.organisation);
    } catch {
      return emptyContext();
    }
  }
  try {
    const raw = window.localStorage.getItem(ORG_CONTEXT_KEY);
    if (!raw) return emptyContext();
    return parseOrgRecord(JSON.parse(raw) as unknown);
  } catch {
    return emptyContext();
  }
}

export function writeOrgContext(ctx: OrgContext): void {
  try {
    window.localStorage.setItem(ORG_CONTEXT_KEY, JSON.stringify(ctx));
  } catch {
    // jsdom/SSR-safe: storage unavailable → no-op, never throws.
  }
}
